import React from "react"
import {
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
} from "recharts"
import { useTranslation } from "gatsby-plugin-react-i18next"
import {
  formatDateToDayMonth,
  formatDateToMonthYearShort,
  formatDateToShortMonthYear,
  formatDateToLongMonthYear,
  formatDateToDayLongMonth,
  formatPercentage,
} from "@utils/formatNumber"
import { formatCurrencyOnlySymbolWithoutDecimals } from "@utils/formatNumber"
import { isMobileResolution } from "../../utils/WindowWidth"

const CustomTooltip = ({ active, payload, label, locale, range, currency }) => {
  const { t } = useTranslation()

  const dateFormatter = value => {
    /*if (range === "YTD" || range > 30) {
      return formatDateToLongMonthYear(value, locale)
    } else {*/
    return formatDateToDayLongMonth(value, locale)
    /*}*/
  }

  if (active && payload && payload.length) {
    const positiveChange = payload[0].payload.diff > 0
    return (
      <div className="custom-tooltip bg-white p-3 border border-slate-300 text-sm">
        <p className="font-bold">{dateFormatter(payload[0].payload.dt)}</p>
        <p>{`${t(
          "chart_tooltip_Price"
        )} ${formatCurrencyOnlySymbolWithoutDecimals(
          payload[0].payload.price,
          locale,
          currency
        )}`}</p>
        <p className={positiveChange ? "text-green-700" : "text-red-700"}>{`${t(
          "chart_tooltip_Change"
        )} ${
          (positiveChange ? "+" : "") +
          formatPercentage(payload[0].payload.diff, locale, 2)
        }`}</p>
      </div>
    )
  }

  return null
}

export const ChartRender = ({
  data,
  range,
  locale = "en-GB",
  currency = "EUR",
}) => {
  const xAxisFormatter = (value, index) => {
    const date = new Date(value)
    if (range === "YTD" || range > 30) {
      return formatDateToMonthYearShort(date, locale)
    } else {
      return formatDateToDayMonth(date, locale)
    }
  }

  const yAxisFormatter = (value, index) => {
    if (value === 0.0) return ""
    else return formatCurrencyOnlySymbolWithoutDecimals(value, locale, currency)
  }

  const tooltipFormatter = (value, name, props) => {
    return formatCurrencyOnlySymbolWithoutDecimals(value, locale, currency)
  }

  const isMobile = isMobileResolution()

  return (
    <div className="relative w-full mt-0">
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart
          data={data.prices}
          margin={{
            top: 20,
            right: isMobile ? 10 : 0,
            left: isMobile ? 10 : 0,
            bottom: 0,
          }}
        >
          <defs>
            <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#286350" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#286350" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey={"dt"}
            tick={{ fontSize: "12px", fontFamily: "Inter" }}
            tickFormatter={xAxisFormatter}
            ticks={data.ticks}
            interval={"preserveStartEnd"}
          />
          <YAxis
            dataKey={"price"}
            tickFormatter={yAxisFormatter}
            tick={{ fontSize: "12px", fontFamily: "Inter" }}
            mirror={true}
            domain={([dataMin, dataMax]) => {
              const min = Math.round(dataMin - dataMin * 0.02)
              const minZeroes = Math.pow(
                10,
                Math.floor(min.toString().length / 2)
              )
              const roundedMin = Math.floor(min / minZeroes) * minZeroes
              const max = Math.round(dataMax + dataMax * 0.02)
              const maxZeroes = Math.pow(
                10,
                Math.floor(max.toString().length / 2)
              )
              const roundedMax = Math.ceil(max / maxZeroes) * maxZeroes
              return [roundedMin, roundedMax]
            }}
          />
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <Tooltip
            content={
              <CustomTooltip
                locale={locale}
                range={range}
                currency={currency}
              />
            }
          />
          <Area
            type="linear"
            dataKey="price"
            stroke="#286350"
            fillOpacity={1}
            fill="url(#colorGradient)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
